<script setup lang="ts">
import { useQueryClient } from "@tanstack/vue-query"
import { useDeleteComment } from "@/api"
import { BaseModal, Button, Card, Icon } from "@/components/ui"
import { useModal, useTranslation } from "@/composables"
import { useEventVaultStore } from "@/stores/eventVaultStore"
import { CommentDeleteModalKey } from "@/symbols"
import { useToast } from "../ui/ToastReka/useToast"

interface CommentDeleteModalProps {
  commentId: number
}

const props = defineProps<CommentDeleteModalProps>()

const eventVaultStore = useEventVaultStore()

const { toggle } = useModal(CommentDeleteModalKey)
const { t } = useTranslation()

const toast = useToast()

const queryClient = useQueryClient()

const { mutate, isPending } = useDeleteComment()

function onDeleteConfirm() {
  if (!eventVaultStore.eventVaultId || !props.commentId) {
    toast.error({
      title: t("something_went_wrong"),
      description: t("comment_delete_modal.missing_id"),
    })
    return
  }

  mutate({ eventVaultId: eventVaultStore.eventVaultId, commentId: props.commentId }, {
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["listComments"] })
      await queryClient.invalidateQueries({ queryKey: ["listMedia"] })

      toast.success({
        title: t("success"),
        description: t("comment_delete_modal.success"),
        icon: "CircleCheck",
      })

      toggle()
    },
    onError: () => toast.error(),
  })
}
</script>

<template>
  <BaseModal :name="CommentDeleteModalKey">
    <Card>
      <template #header>
        <div class="flex justify-between mb-2">
          <h2 class="text-xl font-comfortaa font-bold mb-6">
            {{ t("comment_delete_modal.title") }}
          </h2>
          <Button variant="flat" @click="toggle">
            <Icon icon="Close" />
            <span class="sr-only">{{ t("comment_delete_modal.sr") }}</span>
          </Button>
        </div>
        <p>
          {{ t("comment_delete_modal.message") }}
        </p>
        <div class="flex justify-end gap-4 mt-10">
          <Button variant="flat" @click="toggle">
            {{ t("cancel") }}
          </Button>
          <Button variant="danger" :disabled="isPending" @click="onDeleteConfirm">
            <span>{{ t("confirm") }}</span>
            <Icon v-if="isPending" icon="Loading" />
          </Button>
        </div>
      </template>
    </Card>
  </BaseModal>
</template>
